import React, { useState, useEffect } from 'react';
import useStyles from './useStyles';
import sharedStyles from '../../AdList/useStyles';
import PropTypes from 'prop-types';
import { Button, IconButton, Snackbar, Tooltip } from '@mui/material';
import moment from 'moment';
import axios from 'axios';
import GenericDialog from '../GenericDialog';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';

function AdTable({ jobAds, ads, setAds }) {
  const [token, setToken] = useState(null);
  const [dialogUrl, setDialogUrl] = useState('');
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogDescription, setDialogDescription] = useState('');
  const [dialogButtonText, setDialogButtonText] = useState('');
  const [dialogButtonCallback, setDialogButtonCallback] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSnackOpen, setIsSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const classes = useStyles();
  const sharedClasses = sharedStyles();

  function handleSnack(message) {
    setIsSnackOpen(true);
    setSnackMessage(message);
  }

  useEffect(() => {
    setToken(document.querySelector('meta[name="csrf-token"]').content);
  }, []);

  function handleButtonStyle(ad) {
    switch (ad.status) {
      case 'open':
        return {
          text: 'Expire Ad',
          class: sharedClasses.buttonRedLight,
          callback: () => {
            setDialogUrl(ad.indeed_urls.expire);
            setDialogTitle('Are you sure you want to expire this ad?');
            setDialogDescription(
              'This will remove it from Indeed immediately. To repost this ad later you will be charged for a new ad.'
            );
            setDialogButtonText('Expire now');
            setDialogButtonCallback(() => handleExpire);
            setDialogOpen(true);
          }
        };
      case 'new':
        return {
          text: 'Continue Draft',
          class: sharedClasses.buttonGreenLight,
          callback: () => handleEdit(ad.indeed_urls.edit)
        };
      default:
        return {
          text: 'Clone Ad',
          class: sharedClasses.buttonSecondaryLight,
          callback: () => {
            setDialogUrl(ad.indeed_urls.copy);
            setDialogTitle('Are you sure you want to clone this ad?');
            setDialogDescription('You will have to repost for your ad to be active on Indeed.');
            setDialogButtonText('Clone ad');
            setDialogButtonCallback(() => handleCopy);
            setDialogOpen(true);
          }
        };
    }
  }

  async function handleEdit(url) {
    window.location = url;
  }

  async function handleExpire(url) {
    setDialogLoading(true);
    try {
      const { data } = await axios.put(`${url}`, {
        authenticity_token: token
      });

      handleSnack(`${data.title} has been expired`);
      const newAds = [...jobAds];
      newAds[newAds.findIndex((ad) => ad.id === data.id)].status = 'expired';
      setAds(newAds);
    } catch (e) {
      console.log(e);
      handleSnack('Could not expire ad');
    } finally {
      setDialogLoading(false);
      setDialogUrl(null);
      setDialogOpen(false);
    }
  }

  async function handleArchive(url) {
    setDialogLoading(true);
    try {
      const { data } = await axios.put(`${url}`, {
        authenticity_token: token
      });
      const newAds = [...jobAds];
      newAds[newAds.findIndex((ad) => ad.id === data.id)].status = 'archived';
      setAds(newAds);
      handleSnack(`${data.title} has been archived`);
    } catch (e) {
      console.log(e);
      handleSnack('Could not archive ad');
    } finally {
      setDialogLoading(false);
      setDialogUrl(null);
      setDialogOpen(false);
    }
  }

  async function handleDelete(url) {
    setDialogLoading(true);
    try {
      const { data } = await axios.delete(`${url}`, {
        data: {
          authenticity_token: token
        }
      });
      handleSnack(`${data.title} has been deleted`);
      setAds(jobAds.filter((ad) => ad.id !== data.id));
    } catch (e) {
      console.log(e);
      handleSnack('Could not delete ad');
    } finally {
      setDialogLoading(false);
      setDialogUrl(null);
      setDialogOpen(false);
    }
  }

  async function handleCopy(url) {
    setDialogLoading(true);
    try {
      const { data } = await axios.get(`${url}?operation=clone`);
      window.location = data.url;
    } catch (e) {
      console.log(e);
      handleSnack('Could not clone ad');
    } finally {
      setDialogLoading(false);
      setDialogUrl(null);
      setDialogOpen(false);
    }
  }

  return (
    <div className={classes.table}>
      <div className={`${classes.row} ${classes.rowHeader}`}>
        <span>Job</span>
        <span>Creation Date</span>
        <span>Created By</span>
        <span>Expiry</span>
        <span className={classes.center}>Active on Indeed</span>
        <span className={classes.center}>Sponsored</span>
        <span></span>
        <span></span>
      </div>
      {ads.length > 0 ? (
        ads.map((ad) => (
          <div key={ad.id} className={classes.row}>
            <div className={classes.title}>{ad.title}</div>
            <div>{moment(ad.created_at).format('D MMM YYYY')}</div>
            <div>{ad.created_by}</div>
            <div>{ad.expiry ? moment(ad.expiry).format('D MMM YYYY') : 'No expiry'}</div>
            <div className={`${classes.icon} ${classes.center}`}>
              {ad.status === 'open' ? (
                <CheckCircleIcon className={classes.iconGreen} />
              ) : (
                <CancelIcon className={classes.iconRed} />
              )}
            </div>
            <div className={`${classes.icon} ${classes.center}`}>
              {ad.sponsored ? (
                <CheckCircleIcon className={classes.iconGreen} />
              ) : (
                <div
                  onClick={() =>
                    (window.location = `/admin/indeed/job_ads/${ad.id}/sponsored_job/edit/`)
                  }
                  className={classes.sponsorText}
                >
                  Sponsor Ad
                </div>
              )}
            </div>
            <div className={classes.center}>
              <Button
                style={{ minWidth: 150, padding: '6px 16px' }}
                disableElevation
                size="large"
                classes={{ root: handleButtonStyle(ad).class }}
                onClick={handleButtonStyle(ad).callback}
              >
                {handleButtonStyle(ad).text}
              </Button>
            </div>
            <div className={classes.actionsContainer}>
              <Tooltip title="Edit">
                <IconButton
                  className={`${['expired', 'archived'].includes(ad.status) ? sharedClasses.disableIcon : ''}`}
                  onClick={() => handleEdit(ad.indeed_urls.edit)}
                >
                  <EditIcon fontSize="small" className={classes.iconGrey} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clone">
                <IconButton
                  className={`${['expired', 'archived'].includes(ad.status) ? sharedClasses.disableIcon : ''}`}
                  onClick={() => {
                    setDialogUrl(ad.indeed_urls.copy);
                    setDialogTitle('Are you sure you want to clone this ad?');
                    setDialogDescription(
                      'You will have to repost for your ad to be active on Indeed.'
                    );
                    setDialogButtonText('Clone ad');
                    setDialogButtonCallback(() => handleCopy);
                    setDialogOpen(true);
                  }}
                >
                  <FileCopyIcon className={classes.iconGrey} />
                </IconButton>
              </Tooltip>
              {ad.status === 'expired' ? (
                <Tooltip title="Archive">
                  <IconButton>
                    <ArchiveIcon
                      className={classes.iconRed}
                      onClick={() => {
                        setDialogUrl(ad.indeed_urls.archive);
                        setDialogTitle('Are you sure you want to archive this ad?');
                        setDialogDescription(null);
                        setDialogButtonText('Archive now');
                        setDialogButtonCallback(() => handleArchive);
                        setDialogOpen(true);
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Delete">
                  <IconButton
                    disabled={ad.status === 'open'}
                    className={`${['open', 'archived'].includes(ad.status) ? sharedClasses.disableIcon : ''}`}
                    onClick={() => {
                      setDialogUrl(ad.indeed_urls.delete);
                      setDialogTitle('Are you sure you want to delete this ad?');
                      setDialogDescription(
                        'Your job ad will be permanently deleted and you will not be able to recover your ad details.'
                      );
                      setDialogButtonText('Delete ad');
                      setDialogButtonCallback(() => handleDelete);
                      setDialogOpen(true);
                    }}
                  >
                    <DeleteIcon className={classes.iconRed} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className={classes.row}>
          <div className={classes.title}>No ads to display</div>
        </div>
      )}
      <GenericDialog
        url={dialogUrl}
        title={dialogTitle}
        description={dialogDescription}
        buttonText={dialogButtonText}
        buttonCallback={dialogButtonCallback}
        callbackLoading={dialogLoading}
        isDialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
      />
      <Snackbar open={isSnackOpen} onClose={() => setIsSnackOpen(false)} message={snackMessage} />
    </div>
  );
}

AdTable.propTypes = {
  jobAds: PropTypes.array.isRequired,
  ads: PropTypes.array.isRequired,
  setAds: PropTypes.func.isRequired
};

export default AdTable;
